import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useAppSelector } from '../hooks'
import { useSnackbar } from 'notistack'
import { notificationApi } from '../services/notification'
import { useDispatch } from 'react-redux'

interface WebSocketContextType {
  isConnected: boolean
  lastMessage: any
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined)

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false)
  const [lastMessage, setLastMessage] = useState<any>(null)
  const socketRef = useRef<WebSocket | null>(null)
  const { accessToken, user } = useAppSelector((state) => state.auth)
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()

  const connect = () => {
    if (!accessToken || !user?.id) return

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.host
    // Loại bỏ giao thức (http/https) và path (/api/v1) nếu có để lấy domain
    const wsDomain = baseUrl.replace(/^https?:\/\//, '').split('/')[0]
    const wsUrl = `${protocol}//${wsDomain}/api/v1/ws/notification?staffId=${user.id}&token=${accessToken}`

    console.log('Connecting to WebSocket:', wsUrl)
    const socket = new WebSocket(wsUrl)

    socket.onopen = () => {
      console.log('WebSocket Connected')
      setIsConnected(true)
    }

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data)
      setLastMessage(data)
      console.log('WebSocket Message Received:', data)

      if (data.type === 'NEW_NOTIFICATION') {
        // Hiển thị thông báo nhanh
        enqueueSnackbar(data.payload.content, { 
          variant: 'info',
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        })
        
        // Invalidate cache của notificationApi để cập nhật UI
        dispatch(notificationApi.util.invalidateTags(['Notification']))
      }
    }

    socket.onclose = () => {
      console.log('WebSocket Disconnected')
      setIsConnected(false)
      // Thử kết nối lại sau 5s
      setTimeout(connect, 5000)
    }

    socket.onerror = (error) => {
      console.error('WebSocket Error:', error)
    }

    socketRef.current = socket
  }

  useEffect(() => {
    if (accessToken && user?.id) {
      connect()
    } else {
      if (socketRef.current) {
        socketRef.current.close()
      }
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.close()
      }
    }
  }, [accessToken, user?.id])

  return (
    <WebSocketContext.Provider value={{ isConnected, lastMessage }}>
      {children}
    </WebSocketContext.Provider>
  )
}

export const useWebSocket = () => {
  const context = useContext(WebSocketContext)
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}
